import React from 'react';

import { FormControlLabel, Checkbox as MuiCheckbox, CheckboxProps as MuiCheckboxProps } from '@mui/material';

import styles from './Checkbox.module.scss';

export interface CheckboxProps extends Omit<MuiCheckboxProps, 'onChange'> {
    /**
     * The label for the checkbox
     */
    label?: string;
    /**
     * If true, the checkbox is checked
     */
    checked?: boolean;
    /**
     * The value of the checkbox
     */
    value?: string | number;
    /**
     * The name used to reference the checkbox
     */
    name?: string;
    /**
     * Callback fired when the checkbox is clicked
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
    /**
     * If true, the checkbox will be disabled
     */
    disabled?: boolean;
    /**
     * If true, the checkbox will be in an indeterminate state
     */
    indeterminate?: boolean;
    /**
     * Custom class name for the container
     */
    className?: string;
    /**
     * Position of the label relative to the checkbox
     */
    labelPlacement?: 'start' | 'end' | 'top' | 'bottom';
}

/**
 * Custom Checkbox component based on MUI Checkbox
 * Implements design system styling from Figma atoms
 * Supports light and dark themes via CSS variables
 */
export const Checkbox: React.FC<CheckboxProps> = ({
    label,
    checked,
    value,
    name,
    onChange,
    disabled = false,
    indeterminate = false,
    className = '',
    labelPlacement = 'start',
    ...props
}) => {
    const containerClass = [styles.container, disabled ? styles.disabled : '', className].filter(Boolean).join(' ');

    const Checkbox = (
        <MuiCheckbox
            className={styles.checkbox}
            checked={checked}
            value={value}
            name={name}
            onChange={onChange}
            disabled={disabled}
            indeterminate={indeterminate}
            {...props}
        />
    );

    // If there's a label, wrap in FormControlLabel
    if (label) {
        return (
            <FormControlLabel
                control={Checkbox}
                label={label}
                className={containerClass}
                disabled={disabled}
                labelPlacement={labelPlacement}
                classes={{
                    label: styles.label,
                }}
            />
        );
    }

    // Otherwise, just return the checkbox
    return <div className={containerClass}>{Checkbox}</div>;
};

export default Checkbox;
